import { useEffect, useState } from "react";
import { FormProvider, RHFSelectField, RHFDateRangeField } from "../../common/hook-form"
import { useForm } from "react-hook-form";
import { Alert, Button, Card, CardContent, Grid } from "@mui/material";
import { LoadingButton } from '@mui/lab';
import { useGetAllActiveMerchantsIdAndNameCachedQuery, useGetReportEligibleCourierPartnersQuery } from "../../redux/commonApiCalls";
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { apiConstants, post } from "../../common/constants";
import { downloadFile, formatDate, orderStatusFilter } from "../../common/util";
import useReportsList from "./useReportsList";
import { COURIER_LIST_QUERY_PAYLOAD } from "../../common/ProshipFormComponents";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { addDays, addMonths } from "date-fns";
import _ from "lodash";
import PageTitle from "../../components/PageTitle";
import { useRef } from "react";
import RHFMultipleSelectField from "../../common/hook-form/RHFMultipleSelectField";

// D2C_FORWARD-only filters: courier is also offered on the FM MIS Forward (Pickup Pendency) report.
const COURIER_FILTER_REPORTS = ['D2C_FORWARD', 'PICKUP_PENDENCY_REPORT'];
// "All" uses a real sentinel value, not '' — RHFSelectField's renderValue treats '' as
// "nothing selected" and renders blank/placeholder for it, making "All" look unclickable.
const TERMINAL_OPTIONS = [
    { label: 'All', value: 'ALL' },
    { label: 'Terminal only', value: 'true' },
    { label: 'Non-Terminal only', value: 'false' },
];
const DATE_TYPE_OPTIONS = [
    { label: 'Created Date', value: 'CREATED' },
    { label: 'Pickup Date', value: 'PICKUP' },
    { label: 'Terminal Date', value: 'TERMINAL' },
];

// MIS report types capped to a 31-day range for the merchants below (Comet, Neeman's B2C/B2B).
const DATE_RANGE_LIMITED_REPORTS = ['MIS_FWD', 'MIS_RVP', 'MERCHANT_TERMINAL_MIS', 'BACKEND_MIS', 'D2C_FORWARD'];
const DATE_RANGE_LIMITED_MERCHANT_IDS = [
  '6634c8ad2fd1cb4630e9d1e8', // Neeman's (prod)
  '655c741c0d48cb6aa191d8c1', // Neeman's second account (prod)
  '649c23adca521f1d45138702', // Comet (prod)
  '64d3376ffa398d51c02841ec', // staging test account (Ajeet) — for verifying this cap on staging
];

// The 3 merchants whose volume drives the 31-day cap on an "ALL merchants" run (the 4th id in
// DATE_RANGE_LIMITED_MERCHANT_IDS is a staging-only test account, not part of this check).
const HEAVY_MERCHANT_IDS_FOR_ALL_CAP = [
  '6634c8ad2fd1cb4630e9d1e8', // Neeman's (prod)
  '655c741c0d48cb6aa191d8c1', // Neeman's second account (prod)
  '649c23adca521f1d45138702', // Comet (prod)
];

// The merchantRef field (with allowExclusionsFromAll on) stores "ALL" or "ALL,x,y" — the ids after
// ALL are EXCLUDED from it, not included. Every other shape (a plain string, a comma list of
// specific merchants, or an array) means no ALL selection is in play at all.
function parseMerchantRefExclusions(merchantRef) {
    if (merchantRef === 'ALL') return { isAll: true, excludedIds: [] };
    if (typeof merchantRef === 'string' && merchantRef.startsWith('ALL,')) {
        return { isAll: true, excludedIds: merchantRef.split(',').slice(1).filter(Boolean) };
    }
    return { isAll: false, excludedIds: [] };
}

// Shared with the render-time Alert below, so the "why can't I pick a wider range"
// message and the actual date-picker constraint never drift apart.
function isDateRangeLimited(merchantRef, reportName) {
    if (!DATE_RANGE_LIMITED_REPORTS.includes(reportName)) return false;
    const { isAll, excludedIds } = parseMerchantRefExclusions(merchantRef);
    if (isAll) {
        const allHeavyMerchantsExcluded = HEAVY_MERCHANT_IDS_FOR_ALL_CAP.every((id) => excludedIds.includes(id));
        return !allHeavyMerchantsExcluded;
    }
    const selectedMerchantIds = Array.isArray(merchantRef)
        ? merchantRef
        : (typeof merchantRef === 'string' ? merchantRef.split(',').filter(Boolean) : []);
    return selectedMerchantIds.some((id) => DATE_RANGE_LIMITED_MERCHANT_IDS.includes(id));
}

// Unlocked range once all 3 heavy merchants are excluded from an "ALL merchants" run.
const ALL_MERCHANTS_EXCLUSION_MAX_DAYS = 89;

export default function CreateReport() {
    const { data: allMerchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
    // Excludes inactive couriers and PTL-only-parent couriers server-side — see
    // CourierPartnerController#searchReportEligibleCouriers.
    const { data: allCouriers = [] } = useGetReportEligibleCourierPartnersQuery(COURIER_LIST_QUERY_PAYLOAD)
    // const { user: { userInfo } } = useSelector((state) => state);

    const reportsList = useReportsList()
    let allReports = reportsList.map((v) => ({ label: v.label, value: v.value, description: v.description, tag: v.businessUnit, tagColor: v.tagColor }))

    const { enqueueSnackbar } = useSnackbar();
    const [isSubmitting, setIsSubmitting] = useState(false)
    const navigate = useNavigate()
    const [minDate, setMinDate] = useState(null)
    const [maxDate, setMaxDate] = useState(null)
    const [downloadData, setDownloadData] = useState([])
    const [selectedStatus, setSelectedStatus] = useState('');
    const status = [
        { label: "All", value: 'ALL' },
        { label: "New", value: 'NEW' },
        { label: "DISPUTED BY COURIER", value: 'DISPUTED_BY_COURIER' },
        { label: "DISPUTED BY MERCHANT", value: 'DISPUTED_BY_MERCHANT' },
        { label: "ACCEPTED BY COURIER", value: 'ACCEPTED_BY_COURIER' },
        { label: "ACCEPTED BY MERCHANT", value: 'ACCEPTED_BY_MERCHANT' },
        { label: "REJECTED PROSHIP", value: 'REJECTED_PROSHIP' },
        { label: "ACCEPTED PROSHIP", value: 'ACCEPTED_PROSHIP' },
        { label: "AUTO ACCEPTED BY SYSTEM", value: 'AUTO_ACCEPTED_BY_SYSTEM' }]

    const methods = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                reportName: Yup.string().required("Please select report name"),
                // merchantRef: Yup.string().required("Please select merchant"),
                merchantRef: Yup.mixed()
                    .test('merchantRef-required', 'Please select merchant', function (value) {
                        if (typeof value === 'string') {
                            return value && value.trim() !== '';
                        }
                        if (Array.isArray(value)) {
                            return value.length > 0;
                        }
                        return false;
                    })
                    .test('merchantRef-format', 'Invalid merchant selection', function (value) {
                        if (typeof value === 'string') {
                            return true;
                        }
                        if (Array.isArray(value)) {
                            return value.every(item => typeof item === 'string');
                        }
                        return false;
                    }),
                fromDate: Yup.string().required("From date is required"),
                toDate: (minDate && maxDate) ?
                    Yup.date().typeError("To date is required").required("To date is required").min(minDate).max(maxDate)
                    : Yup.date().typeError("To date is required").required("To date is required")
            })
        ),
        defaultValues: {
            reportName: '',
            fromDate: '',
            toDate: '',
            merchantRef: '',
            courierIds: [],
            statuses: [],
            terminalOnly: 'ALL',
            dateType: 'CREATED'
        }
    })
    const { handleSubmit, watch } = methods

    useEffect(() => {
        const subscription = watch((value) => {
            let newMinDate = null
            let newMaxDate = null
            if (isDateRangeLimited(value.merchantRef, value.reportName) && value.fromDate) {
                newMinDate = value.fromDate
                newMaxDate = addDays(newMinDate, 30)
            } else if (DATE_RANGE_LIMITED_REPORTS.includes(value.reportName) && parseMerchantRefExclusions(value.merchantRef).isAll && value.fromDate) {
                // Unlocked: "ALL" selected but all 3 heavy merchants excluded — allow up to 90 days.
                newMinDate = value.fromDate
                newMaxDate = addDays(newMinDate, ALL_MERCHANTS_EXCLUSION_MAX_DAYS)
            } else if (value.reportName === "PACE_ALLOCATION_REPORT" && value.fromDate) {
                newMinDate = value.fromDate
                newMaxDate = addDays(newMinDate, 7)
            } else if (value.reportName === "ALLOCATION_P2P3_RCA_REPORT" && value.fromDate) {
                // 30-day cap for any merchant selection — matches the Allocation
                // dashboard's own range rule; the backend enforces the same limit.
                newMinDate = value.fromDate
                newMaxDate = addDays(newMinDate, 29)
            } else if ((value.merchantRef === 'ALL' || (typeof value.merchantRef === 'string' && value.merchantRef.includes(','))) && value.fromDate) {
                newMinDate = value.fromDate
                if (value.reportName === "B2B_MERCHANT_MIS_APP_DELIVERY" || value.reportName === "SOW_REPORT_MIS" || value.reportName === "USER_TREND_REPORT") {
                    newMaxDate = addMonths(newMinDate, 3)
                } else {
                    newMaxDate = addDays(newMinDate, 35)
                }
            }
            setMinDate(newMinDate)
            setMaxDate(newMaxDate)

            // A toDate picked before the constraint tightened (e.g. a wide range chosen, then a
            // capped merchant/report selected afterwards) would otherwise sit in the field as an
            // out-of-range value with no visible error until submit. Clear it so the cap holds
            // no matter what order the fields are filled in.
            if (newMaxDate && value.toDate && (new Date(value.toDate) > newMaxDate || new Date(value.toDate) < newMinDate)) {
                methods.setValue('toDate', '', { shouldDirty: true })
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, methods])

    // Set 3-month date range for SOW_REPORT_MIS
    const lastReportNameRef = useRef(null);

    useEffect(() => {
        const subscription = watch((value) => {
            const currentReportName = value.reportName;
            const fromDate = value.fromDate;
            const toDate = value.toDate;

            // Only handle logic when reportName changes
            if (currentReportName !== lastReportNameRef.current) {
                lastReportNameRef.current = currentReportName;

                if ((currentReportName === "SOW_REPORT_MIS" || currentReportName === "USER_TREND_REPORT") && (!fromDate || !toDate)) {
                    const currentDate = new Date();
                    const threeMonthsAgo = addMonths(currentDate, -3);

                    methods.setValue('fromDate', threeMonthsAgo, { shouldDirty: true });
                    methods.setValue('toDate', currentDate, { shouldDirty: true });
                } else if ((currentReportName !== "SOW_REPORT_MIS" && currentReportName !== "USER_TREND_REPORT") && (fromDate || toDate)) {
                    methods.setValue('fromDate', '', { shouldDirty: true });
                    methods.setValue('toDate', '', { shouldDirty: true });
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [watch, methods]);

    useEffect(() => {
        if (downloadData.length > 0) {
            downloadFile(downloadData, 'Weight_Discrepancy')
        }
    }, [downloadData])


    const selectedReportName = watch('reportName');
    const selectedMerchantRef = watch('merchantRef');
    const dateRangeLimited = isDateRangeLimited(selectedMerchantRef, selectedReportName);
    const showCourierFilter = COURIER_FILTER_REPORTS.includes(selectedReportName);
    const showD2cForwardOnlyFilters = selectedReportName === 'D2C_FORWARD';
    // On a capped report, the Merchant Name field itself lets the user exclude specific merchants
    // from "All" (see allowExclusionsFromAll on that field below) — no separate control needed.
    const allowMerchantExclusions = DATE_RANGE_LIMITED_REPORTS.includes(selectedReportName);
    const { isAll: merchantRefIsAll, excludedIds: selectedExcludedMerchantIds } = parseMerchantRefExclusions(selectedMerchantRef);
    const allHeavyMerchantsExcluded = HEAVY_MERCHANT_IDS_FOR_ALL_CAP.every((id) => selectedExcludedMerchantIds.includes(id));

    const onSubmit = async (data) => {
        setIsSubmitting(true)
        var reportName = "";

        if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && data.reportName === "MIS_FWD") {
            reportName = "ALL_MERCHANT_MIS_FWD";
        }
        else if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && data.reportName === "D2C_FORWARD") {
            reportName = "ALL_MERCHANT_D2C_FORWARD";
        }
        else if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && data.reportName === "MIS_RVP") {
            reportName = "ALL_MERCHANT_MIS_RVP"
        }
        else if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && data.reportName === "B2B_MIS") {
            reportName = "ALL_MERCHANT_B2B_MIS"
        }
        else if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && data.reportName === "NON_TERMINAL_MIS") {
            reportName = "ALL_MERCHANT_NON_TERMINAL_MIS"
        }
        else if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && data.reportName === "MERCHANT_TERMINAL_MIS") {
            reportName = "ALL_MERCHANT_TERMINAL_MIS"
        }
        else if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && data.reportName === "B2B_MERCHANT_MIS_APP_DELIVERY") {
            reportName = "B2B_ALL_MERCHANT_MIS_APP_DELIVERY"
        }
        else if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && data.reportName === "MERCHANT_RTO_DELIVERED") {
            reportName = "ALL_MERCHANT_RTO_DELIVERED"
        }
        else {
            reportName = data.reportName
        }

        try {
            if (data.reportName === "Weight_Discrepancy") {
                const payload = {
                    // awbNumber: "",
                    merchantId: data.merchantRef,
                    createdDateGT: formatDate(data.fromDate, { format: 'yyyy-MM-dd' }),
                    createdDateLT: formatDate(data.toDate, { format: 'yyyy-MM-dd' }),
                    statusIn: selectedStatus === 'ALL' ? '' : selectedStatus,
                    limit: 100000,
                    skip: 0
                }
                post(`${apiConstants.WEIGHT_DISCREPANCY}?download=true`, payload).then((res) => {
                    setDownloadData(res?.data)
                    setIsSubmitting(false)
                    if (res.data.status === "SUCCESS") {
                        window.open(res.data?.reportMeta?.s3Link?.signedLink, '_blank')
                        enqueueSnackbar("Report successfully downloaded!", { variant: 'success' })
                    }
                })
                    .catch((err) => {
                        console.log(err)
                        enqueueSnackbar("Some error occurred", { variant: 'error' })
                    })
            }
            else {

                // "ALL,x,y" (allowExclusionsFromAll on the Merchant Name field) unpacks into a clean
                // merchantRef: 'ALL' plus a separate excludedMerchantIds list — the backend has no
                // notion of an "ALL,x,y" merchantRef, so this must never be sent as-is.
                const { isAll, excludedIds } = parseMerchantRefExclusions(data.merchantRef);
                let request = {
                    reportName: reportName,
                    fromDate: formatDate(data.fromDate, { format: 'yyyy-MM-dd' }),
                    toDate: formatDate(data.toDate, { format: 'yyyy-MM-dd' }),
                    merchantRef: isAll ? 'ALL' : (Array.isArray(data.merchantRef) ? data.merchantRef.join(',') : data.merchantRef),
                    overwrite: true
                }
                if (showCourierFilter && data.courierIds?.length > 0 && !data.courierIds.includes('ALL')) {
                    request.courierIds = data.courierIds
                }
                if (showD2cForwardOnlyFilters) {
                    if (data.statuses?.length > 0) {
                        request.statuses = data.statuses
                    }
                    if (data.terminalOnly === 'true' || data.terminalOnly === 'false') {
                        request.terminalOnly = data.terminalOnly === 'true'
                    }
                    if (data.dateType && data.dateType !== 'CREATED') {
                        request.dateType = data.dateType
                    }
                }
                if (isAll && excludedIds.length > 0) {
                    request.excludedMerchantIds = excludedIds
                }
                while (1) {
                    const { data } = await post(apiConstants.CREATE_REPORT_ASYNC, request)
                    if (data.status === "SUCCESS" && data.requsetState === 'COMPLETED') {
                        window.open(data.reportMeta?.s3Link?.signedLink, true)
                        enqueueSnackbar("Report successfully created!", { variant: 'success' })
                        break;
                    }
                    if (data.status === "ERROR" && data.requsetState === 'COMPLETED') {
                        enqueueSnackbar(data?.exception?.message || "Error fetching report", { variant: 'error' })
                        break;
                    }
                    await new Promise(resolve => setTimeout(resolve, 30000));
                }
                navigate('/reports')
            }
        } catch (err) {
            enqueueSnackbar(err.message, { variant: 'error' })
            setIsSubmitting(false)

        }
        setIsSubmitting(false)
    }
    // previously used onsubmit with user validation on few reports
    // const onSubmit = async (data) => {
    //     setIsSubmitting(true)
    //     var reportName = "";

    //     if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && (data.reportName === "NDR" || data.reportName === "NDR_AWB_REG_DT")) {
    //         if (userInfo?.username === "kshitij.magre@prozo.com"
    //             || userInfo?.username === "karmpal.singh@prozo.com"
    //             || userInfo?.username === "prabhmeet.kaur@prozo.com"
    //             || userInfo?.username === "yogesh.kuswaha@prozo.com"
    //             || userInfo?.username === "abhishek.saini@prozo.com"
    //             || userInfo?.username === "irshad.ali@prozo.com"
    //         ) {
    //         } else {
    //             enqueueSnackbar("You are not authorized to generate this report", { variant: 'error' });
    //             setIsSubmitting(false);
    //             return;
    //         }
    //     }

    //     if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && data.reportName === "MIS_FWD") {
    //         if (userInfo?.username === "kshitij.magre@prozo.com" || userInfo?.username === "karmpal.singh@prozo.com" || userInfo?.username === "abhishek.saini@prozo.com") {
    //             reportName = "ALL_MERCHANT_MIS_FWD";
    //         } else {
    //             enqueueSnackbar("You are not authorized to generate this report", { variant: 'error' });
    //             setIsSubmitting(false);
    //             return;
    //         }
    //     }
    //     else if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && data.reportName === "MIS_RVP") {
    //         if (userInfo?.username === "kshitij.magre@prozo.com" || userInfo?.username === "karmpal.singh@prozo.com" || userInfo?.username === "abhishek.saini@prozo.com") {
    //             reportName = "ALL_MERCHANT_MIS_RVP"
    //         } else {
    //             enqueueSnackbar("You are not authorized to generate this report", { variant: 'error' });
    //             setIsSubmitting(false);
    //             return;
    //         }
    //     }
    //     else if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && data.reportName === "B2B_MIS") {
    //         reportName = "ALL_MERCHANT_B2B_MIS"
    //     }
    //     else if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && data.reportName === "NON_TERMINAL_MIS") {
    //         reportName = "ALL_MERCHANT_NON_TERMINAL_MIS"
    //     }
    //     else if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && data.reportName === "MERCHANT_TERMINAL_MIS") {
    //         reportName = "ALL_MERCHANT_TERMINAL_MIS"
    //     }
    //     else if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && data.reportName === "B2B_MERCHANT_MIS_APP_DELIVERY") {
    //         reportName = "B2B_ALL_MERCHANT_MIS_APP_DELIVERY"
    //     }
    //     else if ((data.merchantRef === "ALL" || (typeof data.merchantRef === 'string' && data.merchantRef.includes(','))) && data.reportName === "MERCHANT_RTO_DELIVERED") {
    //         reportName = "ALL_MERCHANT_RTO_DELIVERED"
    //     }
    //     else {
    //         reportName = data.reportName
    //     }

    //     try {
    //         if (data.reportName === "B2C_POD") {
    //             const payload = {
    //                 from_date: formatDate(data.fromDate, { format: 'yyyy-MM-dd' }),
    //                 merchantOId: data.merchantRef,
    //                 shipmentType: "B2C",
    //                 to_date: formatDate(data.toDate, { format: 'yyyy-MM-dd' }),
    //                 type: "ZIP"
    //             };
    //             await handlePODDownload(payload, 'B2C')
    //         } else if (data.reportName === "B2B_POD") {
    //             const payload = {
    //                 from_date: formatDate(data.fromDate, { format: 'yyyy-MM-dd' }),
    //                 merchantOId: data.merchantRef,
    //                 shipmentType: "B2B",
    //                 to_date: formatDate(data.toDate, { format: 'yyyy-MM-dd' }),
    //                 type: "ZIP"
    //             };
    //             await handlePODDownload(payload, 'B2B')
    //         }
    //         else if (data.reportName === "Weight_Discrepancy") {
    //             const payload = {
    //                 // awbNumber: "",
    //                 merchantId: data.merchantRef,
    //                 createdDateGT: formatDate(data.fromDate, { format: 'yyyy-MM-dd' }),
    //                 createdDateLT: formatDate(data.toDate, { format: 'yyyy-MM-dd' }),
    //                 statusIn: selectedStatus === 'ALL' ? '' : selectedStatus,
    //                 limit: 100000,
    //                 skip: 0
    //             }
    //             post(`${apiConstants.WEIGHT_DISCREPANCY}?download=true`, payload).then((res) => {
    //                 setDownloadData(res?.data)
    //                 setIsSubmitting(false)
    //                 if (res.data.status === "SUCCESS") {
    //                     window.open(res.data?.reportMeta?.s3Link?.signedLink, '_blank')
    //                     enqueueSnackbar("Report successfully downloaded!", { variant: 'success' })
    //                 }
    //             })
    //                 .catch((err) => {
    //                     console.log(err)
    //                     enqueueSnackbar("Some error occurred", { variant: 'error' })
    //                 })
    //         }
    //         else {

    //             let request = {
    //                 reportName: reportName,
    //                 fromDate: formatDate(data.fromDate, { format: 'yyyy-MM-dd' }),
    //                 toDate: formatDate(data.toDate, { format: 'yyyy-MM-dd' }),
    //                 // merchantRef: data.merchantRef,
    //                 merchantRef: Array.isArray(data.merchantRef) ? data.merchantRef.join(',') : data.merchantRef,
    //                 overwrite: true
    //             }
    //             while (1) {
    //                 const { data } = await post(apiConstants.CREATE_REPORT_ASYNC, request)
    //                 if (data.status === "SUCCESS" && data.requsetState === 'COMPLETED') {
    //                     window.open(data.reportMeta?.s3Link?.signedLink, true)
    //                     enqueueSnackbar("Report successfully created!", { variant: 'success' })
    //                     break;
    //                 }
    //                 if (data.status === "ERROR" && data.requsetState === 'COMPLETED') {
    //                     enqueueSnackbar(data?.exception?.message || "Error fetching report", { variant: 'error' })
    //                     break;
    //                 }
    //                 await new Promise(resolve => setTimeout(resolve, 30000));
    //             }
    //             navigate('/reports')
    //         }
    //     } catch (err) {
    //         enqueueSnackbar(err.message, { variant: 'error' })
    //         setIsSubmitting(false)

    //     }
    //     setIsSubmitting(false)
    // }


    // to download POD zip file

    // const handlePODDownload = async (payload, shipmentType) => {
    //     try {
    //         const response = await downloadZip(apiConstants.DOWNLOAD_POD, payload, { responseType: 'blob' });
    //         const binaryData = [];
    //         binaryData.push(response.data);
    //         const href = URL.createObjectURL(new Blob(binaryData, { type: "application/zip" }));
    //         const link = document.createElement("a");
    //         link.href = href;
    //         link.setAttribute("download", `POD_${shipmentType}.zip`);
    //         document.body.appendChild(link);
    //         link.click();
    //         document.body.removeChild(link);
    //         URL.revokeObjectURL(href);
    //     } catch (error) {
    //         enqueueSnackbar(error.message, { variant: "error" });
    //         console.error(error);
    //     }
    // }


    return (
        <>
            <PageTitle>Create Report</PageTitle>
            <Card>
                {/* <CardHeader title='Search/Download' /> */}
                <CardContent>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <RHFMultipleSelectField
                                    width="100%"
                                    name="merchantRef"
                                    outputFormat="string"
                                    search={true}
                                    allowExclusionsFromAll={allowMerchantExclusions}
                                    options={
                                        [{ label: 'All', value: 'ALL' }, ...(_.orderBy(allMerchants, 'name').map((mer) => ({ label: mer.name, value: mer.id })))]
                                    }
                                    label="Merchant Name"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <RHFSelectField width="100%" name="reportName" options={allReports}
                                    label="Report Name" />
                            </Grid>
                            <RHFDateRangeField
                                gridSize={6}
                                fromDateProps={{
                                    disabled: selectedReportName === "SOW_REPORT_MIS" || selectedReportName === "USER_TREND_REPORT",
                                    disableFuture: true
                                }}
                                toDateProps={{
                                    disabled: selectedReportName === "SOW_REPORT_MIS" || selectedReportName === "USER_TREND_REPORT",
                                    disableFuture: true,
                                    minDate,
                                    maxDate
                                }}
                            />
                            {selectedReportName === "Weight_Discrepancy" && (
                                <Grid item xs={12} md={6}>
                                    <RHFSelectField
                                        name='selectedStatus'
                                        label='Select Status'
                                        options={status.map((op) => ({
                                            label: op.label.toLowerCase().replace(/(^\w| \w)/g, match => match.toUpperCase()),
                                            value: op.value,
                                        }))}
                                        onChange={(e) => setSelectedStatus(e.target.value)}
                                    />
                                </Grid>
                            )}
                            {showCourierFilter && (
                                <Grid item xs={12} md={6}>
                                    <RHFMultipleSelectField
                                        width="100%"
                                        name="courierIds"
                                        search={true}
                                        options={[
                                            { label: 'All', value: 'ALL' },
                                            ..._.orderBy(allCouriers, 'name').map((cp) => ({
                                                label: `${cp.cpAccountCode} (${cp.prozoId})`,
                                                value: cp.id
                                            }))
                                        ]}
                                        label="Courier"
                                    />
                                </Grid>
                            )}
                            {showD2cForwardOnlyFilters && (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <RHFMultipleSelectField
                                            width="100%"
                                            name="statuses"
                                            search={true}
                                            options={orderStatusFilter().options}
                                            label="Status"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <RHFSelectField width="100%" name="terminalOnly" options={TERMINAL_OPTIONS}
                                            label="Terminal / Non-Terminal" />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <RHFSelectField width="100%" name="dateType" options={DATE_TYPE_OPTIONS}
                                            label="Date Type" />
                                    </Grid>
                                </>
                            )}
                            {dateRangeLimited && (
                                <Grid item xs={12}>
                                    <Alert severity="info">
                                        Note : This report allows a maximum date range of 31 days from the selected start date for this merchant.
                                        {allowMerchantExclusions && merchantRefIsAll && !allHeavyMerchantsExcluded && (
                                            " Uncheck Neeman's (both accounts) and Comet in the Merchant Name field above to unlock a 90-day range for the remaining merchants."
                                        )}
                                    </Alert>
                                </Grid>
                            )}
                            {allowMerchantExclusions && merchantRefIsAll && allHeavyMerchantsExcluded && (
                                <Grid item xs={12}>
                                    <Alert severity="success">
                                        Note : Neeman's and Comet are excluded — this report now allows a maximum date range of 90 days for the remaining merchants.
                                    </Alert>
                                </Grid>
                            )}
                            {(selectedReportName === "PACE_ALLOCATION_REPORT") && (
                                <Grid item xs={12}>
                                    <Alert severity="info">
                                        Note : This report allows a maximum date range of 7 days from the selected start date.
                                    </Alert>
                                </Grid>
                            )}
                            {(selectedReportName === "USER_TREND_REPORT") && (
                                <Grid item xs={12}>
                                    <Alert severity="info">
                                        Note : This Report by default provides the shipment allocation analytics over the past two months to the current month to date.
                                    </Alert>
                                </Grid>
                            )}
                            {(selectedReportName === "SOW_REPORT_MIS") && (
                                <Grid item xs={12}>
                                    <Alert severity="info">
                                        Note : This Report by default compares merchant shipment performance over the past two months to the current month to date.
                                    </Alert>
                                </Grid>
                            )}
                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <LoadingButton loading={isSubmitting} type="submit" size="small" variant="contained">Create Report</LoadingButton>
                                <Button sx={{ ml: 1 }} variant="contained" color="error" size="small" onClick={() => {
                                    methods.reset()
                                }}>Reset</Button>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>
        </>
    )
}
